import React, { type FC } from "react";
import { useNavigate } from "react-router-dom";
import { useWindows } from "@app/overlay";
import { OPEN_FILTER_QUERY_PARAM } from "@app/router/queryParams";
import { CardHeader } from "@shared/ui/card-header";
import { ListItem } from "@src/components/business-list";
import { BusinessList } from "@src/components/business-list";
import { useBusinessListQuery } from "@src/hooks/useBusinessListQuery";
import { BusinessBoStateValue, BusinessDateValue, BusinessDivisionValue } from "@src/components/business-data-display";
import { useUrlFilter } from "@shared/hooks/useUrlFilter";
import { useUrlParamTrigger } from "@shared/hooks/useUrlParamTrigger";
import {
    stockOrdersServices,
    type StockOrder,
    type StockOrderFilter,
    type StockOrderExtends,
} from "../../../../api/stockOrders";
import { StockOrderSupplierValue } from "../../helpers/resolveStockOrderSupplier";
import { stockPlacementListWindows } from "./stockPlacementList.windows";
import "./StockPlacementList.scss";

/** Список приходных ордеров, требующих размещения ТМЦ. */
export const StockPlacementList: FC = () => {
    const windows = useWindows();
    const navigate = useNavigate();
    const filterState = useUrlFilter<StockOrderFilter>();

    const list = useBusinessListQuery<StockOrder, StockOrderFilter>({
        queryKey: "stock.placement.orders",
        filter: filterState.filter,
        queryFn: async (flt, signal) => {
            const result = await stockOrdersServices.load(
                {
                    flt,
                    include: ["Division", "SupplyDivision", "SupplyEnterprise", "State"] satisfies Array<
                        keyof StockOrderExtends
                    >,
                },
                { signal },
            );
            return result;
        },
        getId: (item) => item.Code,
    });

    const openFilterWindow = (): void => {
        stockPlacementListWindows.filter.open(windows, {
            initialFilter: filterState.filter,
            onApply: (flt) => {
                filterState.applyFilter(flt, true);
            },
        });
    };

    useUrlParamTrigger(OPEN_FILTER_QUERY_PARAM, () => {
        openFilterWindow();
    });

    return (
        <div className="stock_placement_list_page">
            <CardHeader
                text="Размещение ТМЦ"
                buttons={[
                    {
                        type: "button",
                        text: "Поиск",
                        srcIcon: "/images/common/options.svg",
                        onClick: openFilterWindow,
                    },
                ]}
            />
            <BusinessList list={list} onOpenFilter={openFilterWindow}>
                {list.data.map((item) => (
                    <ListItem
                        key={item.Code}
                        title={`Приходный ордер №${item.Num}`}
                        props={[
                            { label: "Дата", value: <BusinessDateValue value={item.Date} /> },
                            { label: "Склад", value: <BusinessDivisionValue division={item.Division} /> },
                            { label: "Поставщик", value: <StockOrderSupplierValue order={item} /> },
                            { label: "Статус", value: <BusinessBoStateValue state={item.State} /> },
                        ]}
                        onClick={() => {
                            navigate({ pathname: `${item.Code}` });
                        }}
                    />
                ))}
            </BusinessList>
        </div>
    );
};
